<template>
  <div class="sticky top-6 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
    <!-- Order Summary Header -->
    <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Bestellübersicht') }}</h3>
    
    <!-- Progress to Next Tier or Current Tier Status -->
    <div class="mb-6">
      <div class="flex justify-between items-center mb-2">
        <span class="text-sm text-gray-600">
          <span v-if="progressToNextTier">{{ $t('Fortschritt zu') }} {{ $t('Stufe') }} {{ nextTierName?.replace(/^(Tier|Stufe)\s*/i, '') }}</span>
          <span v-else>{{ $t('Stufe') }} {{ currentTierName?.replace(/^(Tier|Stufe)\s*/i, '') }} {{ $t('Achieved') }}</span>
        </span>
        <span class="text-sm font-medium text-gray-900">
          <span v-if="progressToNextTier">{{ Math.round(progressToNextTier.percentage) }}%</span>
          <span v-else>100%</span>
        </span>
      </div>
      
      <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
        <div 
          class="bg-sonnenglas-600 h-2 rounded-full transition-all duration-300"
          :style="{ width: progressToNextTier ? `${Math.min(progressToNextTier.percentage, 100)}%` : '100%' }"
        ></div>
      </div>
      
      <p class="text-xs text-gray-500">
        <span v-if="progressToNextTier && progressToNextTier.remainingAmount > 0">
          {{ $t('Noch') }} {{ formatCurrency(progressToNextTier.remainingAmount) }} {{ $t('bis') }} {{ $t('Stufe') }} {{ nextTierName?.replace(/^(Tier|Stufe)\s*/i, '') }} {{ $t('Preisstufe') }}
        </span>
        <span v-else-if="progressToNextTier" class="text-sonnenglas-600 font-medium">
          {{ $t('Stufe') }} {{ nextTierName?.replace(/^(Tier|Stufe)\s*/i, '') }} {{ $t('erreicht!') }}
        </span>
        <span v-else class="text-sonnenglas-600 font-medium">
          {{ $t('Höchste Preisstufe erreicht') }}
        </span>
      </p>
    </div>

    <!-- Current Tier -->
    <div class="mb-4 p-3 bg-gray-50 rounded-lg">
      <div class="flex justify-between items-center">
        <span class="text-sm text-gray-600 flex items-center gap-1">{{ $t('Aktuelle Preisstufe') }} <TierInfoTooltip /></span>
        <span class="text-sm font-semibold text-gray-900">{{ $t('Stufe') }} {{ currentTierName?.replace(/^(Tier|Stufe)\s*/i, '') }}</span>
      </div>
    </div>

    <!-- Product Count -->
    <div class="flex justify-between items-center py-2 border-b border-gray-200">
      <span class="text-sm text-gray-600">{{ $t('Artikel') }}</span>
      <span class="text-sm font-medium text-gray-900">{{ totalItems }}</span>
    </div>

    <!-- Shipping cartons & costs, surfaced in the sidebar so they are not missed (feedback #03) -->
    <div class="space-y-2 py-2 border-b border-gray-200">
      <div class="flex justify-between items-center">
        <span class="text-sm text-gray-600 flex items-center gap-1">
          {{ $t('Anzahl Versandkartons') }}
          <!-- Packing-efficiency warning: shown when the last carton is under half full -->
          <span v-if="hasUnderfilledCarton" class="relative group cursor-help inline-flex">
            <svg class="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.492-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left normal-case font-normal">
              {{ $t('Versand-Tipp: Platz optimal nutzen! In deinem Versandkarton ist noch jede Menge Platz. Am effektivsten ist es, immer volle Masterkartons zu bestellen. Nutze die Hinweise (Stück/Karton) direkt am Produkt im Warenkorb.') }}
            </span>
          </span>
        </span>
        <span class="text-sm font-medium text-gray-900">{{ boxQuantity ?? 0 }}</span>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-sm text-gray-600">{{ $t('Versandkosten') }}</span>
        <span class="text-sm text-gray-900">
          <span v-if="palletShipping || !shippingCost">{{ $t('On Request') }}</span>
          <span v-else>{{ formatCurrency(shippingCost) }}</span>
        </span>
      </div>
    </div>

    <!-- Totals -->
    <div class="space-y-2 py-4 border-b border-gray-200">
      <div class="flex justify-between items-center">
        <span class="text-sm text-gray-600">{{ $t('Nettosumme') }}</span>
        <span class="text-sm text-gray-900">{{ formatCurrency(netTotal) }}</span>
      </div>

      <div class="flex justify-between items-center text-base font-semibold">
        <span class="text-gray-900">{{ $t('Bruttosumme') }}</span>
        <span class="text-gray-900">{{ formatCurrency(grossTotal) }}</span>
      </div>
    </div>

    <!-- Place Order Button -->
    <button
      type="button"
      @click="handlePlaceOrder"
      class="w-full mt-6 bg-sonnenglas-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-sonnenglas-500 focus:ring-offset-2 transition-colors"
      :disabled="isDisabled"
      :class="{ 'opacity-50 cursor-not-allowed': isDisabled }"
    >
      {{ $t('Bestellung prüfen') }}
    </button>

    <p v-if="disabledReason" class="text-xs text-gray-500 mt-2 text-center">
      {{ disabledReason }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import TierInfoTooltip from '@/components/TierInfoTooltip.vue'
import { getCartonQuantity } from '@/utils/cartonQuantities'

interface ProgressInfo {
  currentValue: number
  targetValue: number
  remainingAmount: number
  percentage: number
}

interface Product {
  sku: string
  name: string
  price?: { value: number; currency: string }
  fixedNetPrice?: { value: number; currency: string }
  fixedNetPriceLaunch?: { value: number; currency: string }
  excludedFromDiscounts?: boolean
  isPos?: boolean
}

interface OrderedProduct {
  sku: string
  name: string
  quantity: number
  lineTotal: number
}

interface Props {
  currentTierName: string
  nextTierName?: string
  progressToNextTier?: ProgressInfo
  totalItems: number
  productTotal: number
  netTotal: number
  grossTotal: number
  currency: string
  products?: Product[]
  quantities?: Record<string, number>
  productPrices?: Record<string, number>
  boxQuantity?: number
  shippingCost?: number
  palletShipping?: boolean
  isDisabled?: boolean
  disabledReason?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  placeOrder: []
}>()

const orderedProducts = computed<OrderedProduct[]>(() => {
  if (!props.products || !props.quantities || !props.productPrices) {
    return []
  }

  return props.products
    .filter(product => {
      const qty = props.quantities?.[product.sku] || 0
      return qty > 0
    })
    .map(product => {
      const quantity = props.quantities?.[product.sku] || 0
      const unitPrice = props.productPrices?.[product.sku] || 0
      const lineTotal = unitPrice * quantity

      return {
        sku: product.sku,
        name: product.name,
        quantity,
        lineTotal
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
})

// A carton is "under-filled" when the last (partial) carton of any non-POS product is less
// than half full. Surfacing this nudges customers toward ordering full master cartons and
// avoids triggering an extra, half-empty parcel shipped at our own cost (feedback #03).
const hasUnderfilledCarton = computed((): boolean => {
  if (!props.products || !props.quantities) return false
  for (const product of props.products) {
    if (product.isPos) continue
    const qty = props.quantities[product.sku] || 0
    if (qty <= 0) continue
    const cartonQty = getCartonQuantity(product.cartonQuantity)
    if (cartonQty <= 1) continue
    const remainder = qty % cartonQty
    if (remainder > 0 && remainder < cartonQty / 2) {
      return true
    }
  }
  return false
})

const formatCurrency = (amount: number): string => {
  const safeAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: props.currency || 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(safeAmount)
}

const handlePlaceOrder = () => {
  if (!props.isDisabled) {
    emit('placeOrder')
  }
}
</script>